import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, X, Globe, CreditCard, Building2, Server, Search, MoreHorizontal, Trash2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

const STATUS = {
    ok: { dot: "dot-ok", label: "Healthy" },
    warning: { dot: "dot-warn", label: "Warning" },
    critical: { dot: "dot-crit", label: "Critical" },
};

const BRAND_META = {
    elate: { label: "Elate", color: "#0066FF" },
    boxit: { label: "Boxit", color: "#FF5500" },
};

const SERVER_LABELS = {
    "elate-primary": "Elate 20i",
    "boxit-primary": "Boxit 20i",
};

function faviconFor(domain) {
    if (!domain) return null;
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
}

function ClientAvatar({ client, size = 40 }) {
    const url = client.logo || faviconFor(client.website);
    const initial = (client.name || "?").slice(0, 1).toUpperCase();
    return (
        <div
            className="rounded-xl bg-gradient-to-br from-[#0066FF]/30 to-[#FF5500]/30 grid place-items-center shrink-0 overflow-hidden border border-white/[0.06]"
            style={{ width: size, height: size }}
        >
            {url ? (
                <img
                    src={url}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                />
            ) : (
                <span className="text-xs font-display text-white/80">{initial}</span>
            )}
        </div>
    );
}

function BrandPill({ brand }) {
    const b = BRAND_META[brand] || BRAND_META.elate;
    return (
        <span
            className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border font-medium"
            style={{
                color: b.color,
                borderColor: `${b.color}55`,
                background: `${b.color}12`,
            }}
        >
            {b.label}
        </span>
    );
}

function ClientCard({ client, onOpen, onDelete }) {
    const s = STATUS[client.renewal_status] || STATUS.ok;
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [menuOpen]);

    return (
        <motion.div
            data-testid={`client-card-${client.id}`}
            role="button"
            tabIndex={0}
            layout
            onClick={() => onOpen(client)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen(client); }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.25 }}
            className="relative text-left elate-card p-5 hover:border-white/20 transition group cursor-pointer focus:outline-none focus-visible:border-[#0066FF]/60"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <ClientAvatar client={client} />
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{client.name}</div>
                        <div className="text-[11px] text-zinc-500 font-mono truncate">{client.website}</div>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <BrandPill brand={client.brand || "elate"} />
                    <span className={`dot ${s.dot}`} title={s.label} />
                </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-[11px]">
                <div>
                    <div className="text-zinc-500 uppercase tracking-[0.15em] text-[9px]">MRR</div>
                    <div className="font-mono text-sm mt-0.5">£{client.monthly_billing || 0}</div>
                </div>
                <div>
                    <div className="text-zinc-500 uppercase tracking-[0.15em] text-[9px]">Plan</div>
                    <div className="text-sm mt-0.5 truncate">{client.hosting_package}</div>
                </div>
                <div>
                    <div className="text-zinc-500 uppercase tracking-[0.15em] text-[9px]">Renewal</div>
                    <div className="text-sm mt-0.5 font-mono">{client.domain_expires_in_days}d</div>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2 pt-3 border-t border-white/[0.05]">
                <span className="text-[10px] text-zinc-500 flex items-center gap-1.5 truncate">
                    <Server className="h-3 w-3 shrink-0" />
                    {SERVER_LABELS[client.server] || client.server || "—"}
                </span>

                <div className="relative" ref={menuRef}>
                    <button
                        type="button"
                        data-testid={`client-menu-${client.id}`}
                        onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
                        className="p-1 rounded-md text-zinc-500 hover:text-white hover:bg-white/[0.06] transition opacity-0 group-hover:opacity-100 focus:opacity-100"
                        aria-label="Client actions"
                    >
                        <MoreHorizontal className="h-4 w-4" />
                    </button>

                    <AnimatePresence>
                        {menuOpen && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 4 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 4 }}
                                transition={{ duration: 0.12 }}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute right-0 bottom-full mb-1.5 w-44 rounded-xl border border-white/10 bg-[#0E0E11] shadow-2xl p-1 z-30"
                            >
                                <button
                                    type="button"
                                    data-testid={`client-delete-${client.id}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMenuOpen(false);
                                        onDelete(client);
                                    }}
                                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-red-300 hover:bg-red-500/10 hover:text-red-200 transition"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    Delete from CRM
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
}

function ClientDetail({ client, onClose, onUpdated }) {
    const [local, setLocal] = useState(client);
    const [summary, setSummary] = useState(client?.ai_summary || "");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLocal(client);
        setSummary(client?.ai_summary || "");
    }, [client?.id]);

    const patch = async (update) => {
        const optimistic = { ...local, ...update };
        setLocal(optimistic);
        try {
            const { data } = await api.patch(`/clients/${client.id}`, update);
            setLocal(data);
            onUpdated?.(data);
        } catch {
            setLocal(local);
        }
    };

    const generate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post(`/clients/${client.id}/ai-summary`);
            setSummary(data.summary);
        } finally {
            setLoading(false);
        }
    };

    if (!client) return null;

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
                <motion.aside
                    initial={{ x: "100%" }}
                    animate={{ x: 0 }}
                    exit={{ x: "100%" }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative ml-auto w-full max-w-xl bg-[#0A0A0C] border-l border-white/[0.08] h-full overflow-y-auto"
                    data-testid="client-detail-panel"
                >
                    <div className="sticky top-0 backdrop-blur-xl bg-[#0A0A0C]/80 border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                            <ClientAvatar client={local} size={36} />
                            <div className="min-w-0">
                                <div className="font-display text-base truncate">{local.name}</div>
                                <div className="text-[11px] text-zinc-500 font-mono truncate">{local.website}</div>
                            </div>
                        </div>
                        <button data-testid="client-detail-close" onClick={onClose} className="p-2 rounded-lg hover:bg-white/[0.06]">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="p-6 space-y-5">
                        {/* Brand + Server switchers */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="elate-card p-4">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Brand</div>
                                <div className="flex gap-1.5">
                                    {Object.entries(BRAND_META).map(([k, meta]) => (
                                        <button
                                            key={k}
                                            data-testid={`client-brand-${k}`}
                                            onClick={() => patch({ brand: k })}
                                            className="text-xs px-3 py-1.5 rounded-full border transition font-medium"
                                            style={{
                                                borderColor: local.brand === k ? `${meta.color}88` : "rgba(255,255,255,0.08)",
                                                color: local.brand === k ? meta.color : "rgb(161 161 170)",
                                                background: local.brand === k ? `${meta.color}18` : "transparent",
                                            }}
                                        >
                                            {meta.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="elate-card p-4">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
                                    <Server className="h-3 w-3" /> Server
                                </div>
                                <select
                                    data-testid="client-server"
                                    value={local.server || "elate-primary"}
                                    onChange={(e) => patch({ server: e.target.value })}
                                    className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-1.5 text-sm focus:border-[#0066FF] outline-none"
                                >
                                    <option value="elate-primary">Elate 20i</option>
                                    <option value="boxit-primary">Boxit 20i</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>

                        {/* AI Summary */}
                        <div className="elate-ai-border relative rounded-2xl bg-white/[0.02] p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="h-4 w-4 text-[#0066FF]" strokeWidth={1.7} />
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Ana summary</span>
                                </div>
                                <button
                                    data-testid="client-ai-summary-generate"
                                    onClick={generate}
                                    disabled={loading}
                                    className="text-xs rounded-full border border-white/10 px-3 py-1 hover:bg-white/[0.05] disabled:opacity-50"
                                >
                                    {loading ? <Loader2 className="h-3 w-3 animate-spin inline" /> : "Refresh"}
                                </button>
                            </div>
                            <div className="text-sm text-zinc-200 leading-relaxed min-h-[60px]">
                                {summary || <span className="text-zinc-500">Click refresh to generate an AI summary of this client.</span>}
                            </div>
                        </div>

                        {/* Meta grid */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="elate-card p-4">
                                <div className="flex items-center gap-2 text-xs text-zinc-400">
                                    <Building2 className="h-3.5 w-3.5" /> Contact
                                </div>
                                <div className="text-sm mt-1.5">{local.contact || "—"}</div>
                            </div>
                            <div className="elate-card p-4">
                                <div className="flex items-center gap-2 text-xs text-zinc-400">
                                    <CreditCard className="h-3.5 w-3.5" /> Stripe
                                </div>
                                <div className="text-sm mt-1.5 capitalize">{(local.stripe_status || "—").replaceAll("_", " ")}</div>
                            </div>
                            <div className="elate-card p-4">
                                <div className="flex items-center gap-2 text-xs text-zinc-400">
                                    <Globe className="h-3.5 w-3.5" /> Website
                                </div>
                                <div className="text-sm mt-1.5 capitalize">{local.website_status || "—"}</div>
                            </div>
                            <div className="elate-card p-4">
                                <div className="text-xs text-zinc-400">Plan</div>
                                <div className="text-sm mt-1.5">{local.hosting_package || "—"}</div>
                            </div>
                        </div>

                        {local.services?.length > 0 && (
                            <div className="elate-card p-5">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Services</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {local.services.map((svc) => (
                                        <span key={svc} className="text-xs px-2.5 py-1 rounded-full border border-white/[0.08] bg-white/[0.02] text-zinc-200">
                                            {svc}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {local.notes && (
                            <div className="elate-card p-5">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Notes</div>
                                <div className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed">{local.notes}</div>
                            </div>
                        )}
                    </div>
                </motion.aside>
            </motion.div>
        </AnimatePresence>
    );
}

export default function Clients() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [brand, setBrand] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        const { data } = await api.get("/clients");
        setClients(data);
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/clients/${pendingDelete.id}`);
            setClients((prev) => prev.filter((c) => c.id !== pendingDelete.id));
            setPendingDelete(null);
            if (selected?.id === pendingDelete.id) setSelected(null);
        } finally {
            setDeleting(false);
        }
    };

    const brandCounts = useMemo(() => {
        const c = { all: clients.length, elate: 0, boxit: 0 };
        clients.forEach((x) => { c[x.brand || "elate"] = (c[x.brand || "elate"] || 0) + 1; });
        return c;
    }, [clients]);

    const filtered = useMemo(() => {
        return clients.filter((c) => {
            if (brand !== "all" && (c.brand || "elate") !== brand) return false;
            if (statusFilter !== "all" && c.renewal_status !== statusFilter) return false;
            if (q && !`${c.name} ${c.website}`.toLowerCase().includes(q.toLowerCase())) return false;
            return true;
        });
    }, [clients, brand, statusFilter, q]);

    return (
        <div className="space-y-6" data-testid="clients-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">CRM</div>
                    <h2 className="font-display text-3xl mt-2">Clients</h2>
                </div>
                <div className="text-xs text-zinc-400 font-mono">
                    {filtered.length} of {clients.length} clients
                </div>
            </div>

            {/* Brand tabs */}
            <div className="flex flex-wrap items-center gap-2">
                {[
                    ["all", "All", "#ffffff"],
                    ["elate", "Elate", BRAND_META.elate.color],
                    ["boxit", "Boxit", BRAND_META.boxit.color],
                ].map(([k, label, color]) => (
                    <button
                        key={k}
                        data-testid={`clients-brand-${k}`}
                        onClick={() => setBrand(k)}
                        className="text-xs px-4 py-2 rounded-full border transition inline-flex items-center gap-2"
                        style={{
                            borderColor: brand === k ? `${color}88` : "rgba(255,255,255,0.08)",
                            color: brand === k ? (k === "all" ? "#fff" : color) : "rgb(161 161 170)",
                            background: brand === k && k !== "all" ? `${color}12` : brand === k ? "rgba(255,255,255,0.06)" : "transparent",
                        }}
                    >
                        {label}
                        <span className="font-mono text-[10px] text-zinc-500">{brandCounts[k] || 0}</span>
                    </button>
                ))}

                <div className="mx-2 h-5 w-px bg-white/[0.08]" />

                {[
                    ["all", "All"],
                    ["ok", "Healthy"],
                    ["warning", "Warning"],
                    ["critical", "Critical"],
                ].map(([k, l]) => (
                    <button
                        key={k}
                        data-testid={`clients-filter-${k}`}
                        onClick={() => setStatusFilter(k)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition ${
                            statusFilter === k
                                ? "border-white/20 bg-white/[0.06] text-white"
                                : "border-white/[0.08] bg-white/[0.02] text-zinc-400 hover:text-white"
                        }`}
                    >
                        {l}
                    </button>
                ))}

                <div className="ml-auto relative">
                    <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        data-testid="clients-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search domains…"
                        className="bg-[#0E0E11] border border-white/[0.08] rounded-full pl-8 pr-3 py-1.5 text-xs w-52 focus:border-[#0066FF] outline-none"
                    />
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {Array.from({ length: 9 }).map((_, i) => (
                        <div key={i} className="elate-card p-5 h-40 animate-pulse" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="elate-card p-12 text-center text-sm text-zinc-500">
                    No clients match your filters.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filtered.map((c) => (
                        <ClientCard key={c.id} client={c} onOpen={setSelected} onDelete={setPendingDelete} />
                    ))}
                </div>
            )}

            {selected && (
                <ClientDetail
                    client={selected}
                    onClose={() => setSelected(null)}
                    onUpdated={(u) => setClients((p) => p.map((c) => (c.id === u.id ? u : c)))}
                />
            )}

            {/* Delete confirmation */}
            <AnimatePresence>
                {pendingDelete && (
                    <motion.div
                        className="fixed inset-0 z-[70] grid place-items-center px-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        data-testid="client-delete-confirm"
                    >
                        <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => !deleting && setPendingDelete(null)} />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                            className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0E0E11] p-6"
                        >
                            <div className="flex items-start gap-4">
                                <div className="h-10 w-10 rounded-xl bg-red-500/15 border border-red-500/25 grid place-items-center shrink-0">
                                    <AlertTriangle className="h-5 w-5 text-red-400" strokeWidth={1.7} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-display text-lg">Are you sure?</h3>
                                    <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
                                        This will permanently remove{" "}
                                        <span className="text-white font-medium">{pendingDelete.name}</span>{" "}
                                        <span className="font-mono text-zinc-500 text-xs">({pendingDelete.website})</span>{" "}
                                        from the CRM. This can't be undone.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-2">
                                <button
                                    onClick={() => setPendingDelete(null)}
                                    disabled={deleting}
                                    data-testid="client-delete-cancel"
                                    className="text-xs px-4 py-2 rounded-full border border-white/[0.08] hover:bg-white/[0.04] transition disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={deleting}
                                    data-testid="client-delete-confirm-btn"
                                    className="text-xs px-4 py-2 rounded-full bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 inline-flex items-center gap-2 transition"
                                >
                                    {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                    Delete client
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
